// ---- Cooking FX kit (plan 25) --------------------------------------------------
// The shared visual vocabulary of the pan-minigame rework: the flicker-scaled
// toon flame cones (lifted verbatim from the old conjured-campfire FX) and the
// PAN RIG — flames licking under an iron pan with a `dishAnchor` the raw
// ingredient rides. Three consumers, ONE look: the local cook's pan (cooking.ts),
// the remote-player pan mirror (a NetPlayer.cooking/hx/hz broadcast → the same
// rig), and the deployed campfire's live flames (campfire.ts adds makeFlames()
// on top of the static deco model). THREE + toon only — no game/net/state
// imports, so anything (incl. the studio) can build these.
import * as THREE from "three";
import { toonMat } from "./toon.ts";

/** Where the range deco's iron cooktop tops out (src/deco/range.ts — the slab
 *  at y 1.17 h 0.14). A pan rig anchored on a range sits at this height. */
export const RANGE_COOKTOP_Y = 1.24;

/** The dishAnchor's height inside a pan rig (pan surface + a hair). */
export const PAN_DISH_Y = 0.38;

/** Flicker-scaled flame pair (outer orange + inner yellow cone) — the exact
 *  recipe the old cook-anywhere campfire tuned. `scale` sizes the whole flame
 *  (0.6 = a burner under a pan, 1 = a campfire). Animate with updateFlames. */
export function makeFlames(scale = 1): THREE.Group {
  const g = new THREE.Group();
  const outer = new THREE.Mesh(new THREE.ConeGeometry(0.22, 0.5, 8), toonMat(0xe8631f));
  outer.position.y = 0.34 * scale;
  const inner = new THREE.Mesh(new THREE.ConeGeometry(0.12, 0.34, 8), toonMat(0xf6c62c));
  inner.position.y = 0.3 * scale;
  outer.scale.setScalar(scale);
  inner.scale.setScalar(scale);
  g.add(outer, inner);
  g.userData.flameOuter = outer;
  g.userData.flameInner = inner;
  g.userData.flameScale = scale;
  return g;
}

/** Per-frame flame flicker (same waveform the old campfire used). Works on any
 *  Group makeFlames built — pass the wall-clock t. */
export function updateFlames(g: THREE.Object3D, t: number): void {
  const s = (g.userData.flameScale as number) ?? 1;
  const outer = g.userData.flameOuter as THREE.Object3D | undefined;
  const inner = g.userData.flameInner as THREE.Object3D | undefined;
  if (outer) {
    outer.scale.set(s, s * (1 + Math.sin(t * 18) * 0.12 + Math.sin(t * 7) * 0.06), s);
    outer.rotation.y = t * 0.7;
  }
  if (inner) inner.scale.set(s, s * (1 + Math.sin(t * 22 + 1) * 0.15), s);
}

/** The pan over fire: burner flames under an iron skillet, with `dishAnchor`
 *  (userData) for the raw ingredient's mesh. Anchor the GROUP at the cook
 *  surface — a range's cooktop (RANGE_COOKTOP_Y up its base) or just above the
 *  ground over a campfire — and the rig reads right at both. */
export function makePanRig(): THREE.Group {
  const g = new THREE.Group();
  const flames = makeFlames(0.6);
  g.add(flames);
  const iron = toonMat(0x3a3840);
  const ironDk = toonMat(0x27252c);
  // Skillet: dish body + a slight rim + a rod handle.
  const pan = new THREE.Mesh(new THREE.CylinderGeometry(0.26, 0.22, 0.06, 14), iron);
  pan.position.y = 0.31;
  const rim = new THREE.Mesh(new THREE.TorusGeometry(0.25, 0.022, 6, 18), ironDk);
  rim.rotation.x = Math.PI / 2;
  rim.position.y = 0.345;
  const handle = new THREE.Mesh(new THREE.BoxGeometry(0.3, 0.03, 0.05), ironDk);
  handle.position.set(0.38, 0.34, 0);
  g.add(pan, rim, handle);
  const dishAnchor = new THREE.Group();
  dishAnchor.position.y = PAN_DISH_Y;
  g.add(dishAnchor);
  g.userData.dishAnchor = dishAnchor;
  g.userData.flames = flames;
  return g;
}
